import React from 'react'; // eslint-disable-line
import { connect } from 'react-redux';
import { withStyles, Grid, Typography, Paper, Divider, Box } from '@material-ui/core';
import { ExpansionPanel } from '../../../../../../forms/form-components/expansion-panel/expansion-panel';
import styles, { StyledComponent } from '../../../../../../styles/styles';
import { LabeledValue } from '../../../../../../controls/labeled-value';
import { State } from '../../../../../../redux/root-reducer';
import { getFormDetails } from '../../form-details-reducer';
import { FormDetails } from '../../form-details-reducer';
import { OpenModal, openModalAction } from '../../../../../../form-modal/actions';
import { getClaim } from '../../../../../../claim';
import { Nullable } from '../../../../../../shared-types';
import { ClaimType } from '../../../../../../claim/claim-types';

interface BaseProps {
    formDetails?: FormDetails;
    disableEditButton?: boolean;
    claimType?: Nullable<ClaimType>;
}

interface Props extends BaseProps, StyledComponent {
    readonly openModal: OpenModal;
}

const criticalIllnessDeclarationTemplate = `
<p>I, (full name and surname), ID number,</p>
<p>hereby warrant and declare that the foregoing answers and statements are true to the best of my knowledge and belief, and that I have withheld no material fact from Liberty. I further declare that the condition giving rise to this claim, was not due in any way to self inflicted injury or use of alcohol or drugs of any kind, and that I am not insolvent.</p>
<p>I agree that the written statements and affidavits of all the doctors who attended or treated the life assured and all other papers submitted in support of this claim, shall constitute and are hereby made a part of this claim, and further agree that the supply of this form, or any other forms supplemental hereto by Liberty, shall not constitute any admission by it that there is any assurance in force on the life in question or a waiver of any of its rights or defences in law.</p>
<p>I acknowledge and agree that any benefits payable in respect of this claim shall be fofeited if I, or anyone acting on my behalf or with my knowledge or consent, have knowingly withheld any material fact or submitted any false information in respect of this claim. I further agree that upon payment of the benefits hereby claimed, Liberty shall be discharged from all liability in respect of such benefit.</p>
<p>I hereby authorise any medical practitioners, hospitals, clinics, medical aid schemes, pathology, pharmacies, SARS, any other financial institutions, or any other source to furnish to Liberty, or its representative any details relating to any illness or injury to the life assured or such other information as may be necessary to consider this claim. I know and understand the confidential nature of medical information. By appending my signatures at the end of this declaration, I am agreeing that I have given permission to Liberty to obtain medical information and evidence from and/or through third parties without it being seen as a breach of my right of privacy and confidentiality. I further agree that any authorised medical personnel or practitioner may release confidential information to Liberty or other person acting on their behalf and, in such manner, or method as Liberty may direct.</p>
<p>I indemnify Liberty and its directors, agents, and employees against any claim of whatever nature which may be made against them as a result of or arising out of the furnishing of such information. Where the conditions of the policy so allow, I irrevocably authorise Liberty to deduct any expenses incurred by it in respect of this claim and for which I am liable from the benefits payable under the policy.</p>
<p><strong>In the event that a claimant is both the life assured and the policyholder of the policy and is incapable of managing his/her own affairs, an appointment of a curator bonis will be required in order for Liberty to further assess the claim.</strong></p>
`;

const DeclarationViewComponent: React.FunctionComponent<Props> = ({
    classes,
    openModal,
    formDetails,
    disableEditButton,
    claimType,
}: Props) => {
    const declarationInformation = formDetails?.declarationInformation;
    const declarationInfo = formDetails?.declaration || {};
    const isCriticalIllnessClaim =
        claimType === ClaimType.Dread || claimType === ClaimType.TerminalIllness;
    const declarationContentForDisplay = isCriticalIllnessClaim
        ? criticalIllnessDeclarationTemplate
        : declarationInformation || '';

    return (
        <ExpansionPanel name="Declaration Details" style={{ marginLeft: -24, marginRight: -24 }}>
            <React.Fragment>
                <Grid
                    container
                    justify="space-between"
                    alignItems="center"
                    className={classes.marginTop2}
                >
                    <Typography style={{ padding: 10 }} className={classes.heading} variant="body1">
                        Declaration Details
                    </Typography>
                </Grid>

                <Paper className={classes.paper} style={{ padding: '16px 24px', margin: '0 16px' }}>
                    <Grid container item xs={12} justify="flex-end" alignItems="center"></Grid>

                    {claimType !== ClaimType.Disability && (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <LabeledValue
                                    label=""
                                    value={
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: declarationContentForDisplay,
                                            }}
                                        />
                                    }
                                />
                            </Grid>
                        </Grid>
                    )}
                    {claimType === ClaimType.Disability && (
                        <Box>
                            <Box mb={3}>
                                <Typography
                                    variant="h6"
                                    style={{
                                        fontWeight: 600,
                                        fontSize: '1.1rem',
                                        marginBottom: '8px',
                                    }}
                                >
                                    Living Lifestyle Claim Declaration
                                </Typography>
                                <Divider style={{ marginBottom: '16px' }} />

                                <Typography
                                    variant="body1"
                                    style={{
                                        fontWeight: 500,
                                        marginBottom: '24px',
                                    }}
                                >
                                    I, {declarationInfo.firstNameAndSurname || ''},{' '}
                                    {declarationInfo.idNumber || ''}
                                </Typography>
                            </Box>

                            <Box mb={4}>
                                <Typography
                                    variant="body2"
                                    paragraph
                                    style={{
                                        lineHeight: '1.6',
                                        textAlign: 'justify',
                                    }}
                                >
                                    hereby warrant and declare that the foregoing answers and
                                    statements are true to the best of my knowledge and belief, and
                                    that I have withheld no material fact(s) from Liberty. I further
                                    declare that the condition giving rise to this claim, was not
                                    due in any way to self inflicted injury or use of alcohol or
                                    drugs of any kind, and that I am not insolvent.
                                </Typography>

                                <Typography
                                    variant="body2"
                                    paragraph
                                    style={{
                                        lineHeight: '1.6',
                                        textAlign: 'justify',
                                    }}
                                >
                                    I agree that the written statements and affidavits of all the
                                    doctors who attended or treated the Life Assured and all other
                                    papers submitted in support of this claim, shall constitute and
                                    are hereby made a part of this claim, and further agree that the
                                    supply of this form, or any other forms supplemental hereto by
                                    Liberty, shall not constitute any admission by it that there is
                                    any assurance in force on the life in question or a waiver of
                                    any of its rights or defences in law.
                                </Typography>

                                <Typography
                                    variant="body2"
                                    paragraph
                                    style={{
                                        lineHeight: '1.6',
                                        textAlign: 'justify',
                                    }}
                                >
                                    I acknowledge and agree that any benefits payable in respect of
                                    this claim shall be forfeited if I, or anyone acting on my
                                    behalf or with my knowledge or consent, have knowingly withheld
                                    any material fact(s) or submitted any false information in
                                    respect of this claim. I further agree that upon payment of the
                                    benefits hereby claimed, Liberty shall be discharged from all
                                    liability in respect of such benefit. I hereby authorise any
                                    medical practitioners, hospitals, clinics, medical aid schemes,
                                    pathology, pharmacies, SARS, any other financial institutions or
                                    any other source to furnish to Liberty, or its representative
                                    any details relating to any illness or injury to the Life
                                    Assured or such other information as may be necessary to
                                    consider this claim. I know and understand the confidential
                                    nature of medical information. By appending my signatures at the
                                    end of this declaration, I am agreeing that I have given
                                    permission to Liberty to obtain medical information and evidence
                                    from and/or through third parties without it being seen as a
                                    breach of my right of privacy and confidentiality. I further
                                    agree that any authorised medical personnel or practitioner may
                                    release confidential information to Liberty or other person
                                    acting on their behalf and in such manner or method as Liberty
                                    may direct.
                                </Typography>

                                <Typography
                                    variant="body2"
                                    paragraph
                                    style={{
                                        lineHeight: '1.6',
                                        textAlign: 'justify',
                                    }}
                                >
                                    I indemnify Liberty and its directors, agents and employees
                                    against any claim of whatever nature which may be made against
                                    them as a result of or arising out of the furnishing of such
                                    information. Where the conditions of the policy so allow, I
                                    irrevocably authorise Liberty to deduct any expenses incurred by
                                    it in respect of this claim and for which I am liable from the
                                    benefits payable under the policy.{' '}
                                    <b>
                                        In the event that a claimant is both the Life Assured and
                                        the Policyholder of the policy and is incapable of managing
                                        his/her own affairs, an appointment of a curator bonis will
                                        be required in order for Liberty to further assess the
                                        claim. Liberty will not be responsible for the costs
                                        involved in the application or appointment of such curator
                                    </b>
                                    .
                                </Typography>
                            </Box>

                            <Box mt={4} pt={3} style={{ borderTop: '1px solid #e0e0e0' }}>
                                <Typography
                                    variant="h6"
                                    style={{
                                        fontWeight: 600,
                                        fontSize: '1.1rem',
                                        marginBottom: '16px',
                                    }}
                                >
                                    Acceptance of Terms and Conditions
                                </Typography>

                                <Paper
                                    variant="outlined"
                                    style={{
                                        padding: '16px',
                                        backgroundColor: '#fafafa',
                                        borderColor: '#ddd',
                                    }}
                                >
                                    <Box display="flex" alignItems="center">
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                border: '2px solid #000',
                                                borderRadius: '3px',
                                                marginRight: '12px',
                                                backgroundColor: declarationInfo?.agreedToTermsAndConditions
                                                    ? '#002B99'
                                                    : 'transparent',
                                            }}
                                        >
                                            {declarationInfo?.agreedToTermsAndConditions && (
                                                <span
                                                    style={{
                                                        color: '#fff',
                                                        fontSize: '14px',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    ✓
                                                </span>
                                            )}
                                        </Box>
                                        <Typography
                                            variant="body1"
                                            style={{
                                                fontWeight: 500,
                                                fontSize: '0.95rem',
                                            }}
                                        >
                                            I declare that the answers and statements are true to
                                            the best of my knowledge and belief, and that I have
                                            withheld no material fact.
                                        </Typography>
                                    </Box>
                                </Paper>
                            </Box>
                        </Box>
                    )}
                </Paper>
                <br />
            </React.Fragment>
        </ExpansionPanel>
    );
};

const mapStateToProps = (state: State, ownProps: BaseProps) => ({
    formDetails: ownProps.formDetails || getFormDetails(state),
    disableEditButton: ownProps.disableEditButton || false,
    claimType: getClaim(state).claimType,
});

const mapDispatchToProps = {
    openModal: openModalAction,
};

export const DeclarationInformationView = connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(DeclarationViewComponent));
